<script>
import Spinner from "@/components/ui/Spinner";
import indicationService from "@/services/indication";

const PAGE_SIZE = 20;

export default {
  name: "RepeatMedicationModal",

  components: {
    Spinner,
  },

  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    patientId: {
      type: String,
      default: null,
    },
  },

  data() {
    return {
      // Sin rango por defecto: se trae todo el historial del paciente. El
      // selector de fechas queda como filtro opcional para acotarlo.
      dateRange: null,
      docs: [],
      total: 0,
      page: 1,
      pageSize: PAGE_SIZE,
      loading: false,
      expandedId: null,
    };
  },

  computed: {
    localVisible: {
      get() {
        return this.visible;
      },
      set(value) {
        this.$emit("update:visible", value);
      },
    },
  },

  watch: {
    visible(value) {
      if (value) {
        this.page = 1;
        this.load();
      }
    },

    dateRange() {
      if (!this.visible) return;
      this.page = 1;
      this.load();
    },
  },

  methods: {
    async load() {
      if (!this.patientId) {
        console.error("RepeatMedicationModal: falta patientId");
        return;
      }
      this.loading = true;
      this.expandedId = null;
      try {
        const [fromDate, toDate] = this.dateRange || [];
        // yyyy-MM-dd parsea como medianoche UTC, que en ART (UTC-3) cae en
        // el día anterior a las 21:00 — construimos los límites del día en
        // hora local para no cortar afuera prescripciones de "hoy".
        let from, to;
        if (fromDate) {
          const [y, m, d] = fromDate.split("-").map(Number);
          from = new Date(y, m - 1, d, 0, 0, 0, 0).toISOString();
        }
        if (toDate) {
          const [y, m, d] = toDate.split("-").map(Number);
          to = new Date(y, m - 1, d, 23, 59, 59, 999).toISOString();
        }
        const res = await indicationService.getMedicationHistory({
          patient: this.patientId,
          from,
          to,
          offset: (this.page - 1) * this.pageSize,
          limit: this.pageSize,
        });
        this.docs = res.docs || [];
        this.total = res.total || 0;
      } catch (error) {
        console.error("Error loading medication history:", error);
        this.$message.error("Error al cargar el historial de prescripciones");
      } finally {
        this.loading = false;
      }
    },

    onPageChange(newPage) {
      this.page = newPage;
      this.load();
    },

    selectDoc(doc) {
      this.$emit("select", doc);
      this.localVisible = false;
    },

    toggleExpand(doc) {
      this.expandedId = this.expandedId === doc._id ? null : doc._id;
    },

    isExpanded(doc) {
      return this.expandedId === doc._id;
    },

    medicationName(doc) {
      return (
        (doc.publicMedication && doc.publicMedication.name) || "Medicamento"
      );
    },

    dosageOf(doc) {
      return doc.dosage ?? doc.posology ?? "";
    },

    yesNo(value) {
      return value ? "Sí" : "No";
    },
  },
};
</script>

<template lang="pug">
el-dialog(
  title="Repetir prescripción de medicamento"
  :visible.sync="localVisible"
  width="600px"
  custom-class="repeat-medication-dialog"
  center
)
  .repeat-medication-modal__filters
    label.repeat-medication-modal__filters-label Acotar por fecha (opcional)
    el-date-picker(
      v-model="dateRange"
      type="daterange"
      range-separator="-"
      start-placeholder="Desde"
      end-placeholder="Hasta"
      value-format="yyyy-MM-dd"
      :clearable="true"
    )

  .repeat-medication-modal__divider

  spinner(v-if="loading && !docs.length")

  .empty-state(v-if="!loading && !docs.length")
    micon(name="assignment_turned_in" width="32" height="32" color="#bdbdbd")
    p.empty-state__text {{ dateRange ? "No hay prescripciones en el rango seleccionado" : "Este paciente no tiene prescripciones anteriores" }}

  template(v-if="docs.length")
    p.repeat-medication-modal__count {{ total }} {{ total === 1 ? "prescripción encontrada" : "prescripciones encontradas" }}

    ul.repeat-medication-modal__list(:class="{ 'is-refreshing': loading }")
      li.repeat-medication-modal__item(
        v-for="doc in docs"
        :key="doc._id"
        :class="{ 'is-expanded': isExpanded(doc) }"
      )
        .repeat-medication-modal__item-row(@click="selectDoc(doc)")
          .repeat-medication-modal__item-main
            span.repeat-medication-modal__name {{ medicationName(doc) }}
            .repeat-medication-modal__chips
              span.chip(v-if="doc.quantity") Cantidad: {{ doc.quantity }}
              span.chip(v-if="doc.diagnosis && doc.diagnosis.name") {{ doc.diagnosis.name }}
          .repeat-medication-modal__item-side
            span.repeat-medication-modal__date {{ doc.createdAt | formatDate }}
            button.repeat-medication-modal__chevron-btn(
              type="button"
              @click.stop="toggleExpand(doc)"
            )
              micon.repeat-medication-modal__chevron(
                name="expand_more"
                width="18"
                height="18"
                color="#bdbdbd"
                :class="{ 'is-expanded': isExpanded(doc) }"
              )

        .repeat-medication-modal__detail(v-if="isExpanded(doc)")
          .repeat-medication-modal__detail-row
            span.repeat-medication-modal__detail-label Medicamento
            span.repeat-medication-modal__detail-value {{ medicationName(doc) }}{{ doc.publicMedication && doc.publicMedication.presentation ? ` — ${doc.publicMedication.presentation}` : "" }}
          .repeat-medication-modal__detail-row(v-if="doc.quantity")
            span.repeat-medication-modal__detail-label Cantidad
            span.repeat-medication-modal__detail-value {{ doc.quantity }}
          .repeat-medication-modal__detail-row(v-if="dosageOf(doc)")
            span.repeat-medication-modal__detail-label Dosis
            span.repeat-medication-modal__detail-value {{ dosageOf(doc) }}
          .repeat-medication-modal__detail-row(v-if="doc.diagnosis && doc.diagnosis.name")
            span.repeat-medication-modal__detail-label Diagnóstico
            span.repeat-medication-modal__detail-value {{ doc.diagnosis.name }}
          .repeat-medication-modal__detail-row
            span.repeat-medication-modal__detail-label Uso prolongado
            span.repeat-medication-modal__detail-value {{ yesNo(doc.isLongTerm) }}
          .repeat-medication-modal__detail-row
            span.repeat-medication-modal__detail-label Genérico
            span.repeat-medication-modal__detail-value {{ yesNo(doc.generic) }}
          .repeat-medication-modal__detail-row(v-if="doc.observations")
            span.repeat-medication-modal__detail-label Observaciones
            span.repeat-medication-modal__detail-value {{ doc.observations }}
          .repeat-medication-modal__detail-row
            span.repeat-medication-modal__detail-label Fecha
            span.repeat-medication-modal__detail-value {{ doc.createdAt | formatDate }}
          button.repeat-medication-modal__detail-select(type="button" @click="selectDoc(doc)")
            | Repetir esta prescripción

    el-pagination.repeat-medication-modal__pagination(
      v-if="total > pageSize"
      layout="prev, pager, next"
      :current-page="page"
      :page-size="pageSize"
      :total="total"
      :disabled="loading"
      @current-change="onPageChange"
    )
</template>

<style lang="scss" scoped>
// el-dialog's own template root IS .el-dialog__wrapper, so it already
// carries this component's scope attribute (Vue 2 applies a child
// component's root element the parent's scoped attribute) — ::v-deep here
// would compile to a descendant selector and never match its own root.
.el-dialog__wrapper {
  display: flex;
  align-items: center;
  justify-content: center;
}

// .repeat-medication-dialog (via custom-class) lands on .el-dialog, which
// IS a genuine descendant of the wrapper root — this one does need ::v-deep.
::v-deep .repeat-medication-dialog {
  margin: 0 !important;
  max-height: 90vh;
  display: flex;
  flex-direction: column;

  .el-dialog__body {
    overflow-y: auto;
  }
}

.repeat-medication-modal {
  &__filters {
    display: flex;
    flex-direction: column;
    gap: 0.4rem;
    align-items: flex-start;
  }

  &__filters-label {
    font-size: 0.8rem;
    font-weight: bold;
    color: $lynch;
    text-transform: uppercase;
    letter-spacing: 0.03em;
  }

  &__divider {
    height: 1px;
    margin: 1.1rem 0;
    background: linear-gradient(to right, transparent, $gainsboro, transparent);
  }

  &__count {
    color: $lynch;
    font-size: 0.85rem;
    margin: 0 0 0.6rem;
  }

  &__list {
    list-style: none;
    margin: 0;
    padding: 0;
    max-height: 360px;
    overflow-y: auto;
    border: 1px solid $whisper;
    border-radius: 10px;
    transition: opacity 150ms;

    &.is-refreshing {
      opacity: 0.6;
    }
  }

  &__item {
    border-bottom: 1px solid $whisper;

    &.is-expanded {
      background-color: $alabaster;
    }

    &:last-child {
      border-bottom: none;
    }
  }

  &__item-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 1rem;
    padding: 0.85rem 1rem;
    cursor: pointer;
    transition: background-color 150ms;

    &:hover {
      background-color: $alabaster;
    }
  }

  &__chevron-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0.5rem;
    margin: -0.5rem 0;
    border: none;
    background: transparent;
    cursor: pointer;
    border-radius: 50%;
    transition: background-color 150ms;

    &:hover {
      background-color: $whisper;
    }
  }

  &__chevron {
    transition: transform 150ms;
    pointer-events: none;

    &.is-expanded {
      transform: rotate(180deg);
    }
  }

  &__item-main {
    display: flex;
    flex-direction: column;
    gap: 0.35rem;
    min-width: 0;
  }

  &__name {
    font-weight: 600;
    color: $primary-alt;
  }

  &__chips {
    display: flex;
    flex-wrap: wrap;
    gap: 0.35rem;
  }

  &__item-side {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    flex-shrink: 0;
  }

  &__date {
    color: $lynch;
    font-size: 0.85rem;
    white-space: nowrap;
  }

  &__pagination {
    margin-top: 1rem;
    text-align: center;
  }

  &__detail {
    padding: 0 1rem 1rem;
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
  }

  &__detail-row {
    display: flex;
    gap: 0.5rem;
    font-size: 0.85rem;
  }

  &__detail-label {
    flex: 0 0 130px;
    font-weight: 600;
    color: $lynch;
  }

  &__detail-value {
    color: $primary-alt;
    word-break: break-word;
  }

  &__detail-select {
    align-self: flex-start;
    margin-top: 0.3rem;
    padding: 6px 14px;
    border: none;
    border-radius: 6px;
    background-color: $primary;
    color: #fff;
    font-size: 0.85rem;
    font-weight: 600;
    cursor: pointer;
    transition: opacity 150ms;

    &:hover {
      opacity: 0.85;
    }
  }
}

.chip {
  background-color: $solitude;
  color: $lynch;
  border-radius: 4px;
  padding: 5px 10px;
  font-size: 0.75rem;
  font-weight: bold;
}

.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
  padding: 3rem 1rem;
  background-color: $alabaster;
  border-radius: 10px;
}

.empty-state__text {
  color: $lynch;
  font-size: 1rem;
  margin: 0;
}
</style>
